//@flow
import { tradeLoyaltyStatuses } from './constants';
import SV from 'server-vars';
import { defineMessages, intlShape } from 'react-intl';
import { stringify } from 'qs';
import { noticeError } from './shared/helpers/newrelic';

// a trade firm with "NOT_ELIGIBLE" loyalty status is eligible to submit the loyaly enrollment form as of release 155
export function isEligibleLoyaltyStatus(loyaltyStatus: ?string) {
    return [tradeLoyaltyStatuses.ELIGIBLE, tradeLoyaltyStatuses.NOT_ELIGIBLE].includes(
        loyaltyStatus
    );
}

export function setupDeepLinkHandler(callCount: number) {
    const hash = window.location.hash;
    if (!hash) {
        return {
            onCallCompleted: () => {},
            registerDeepLink: () => {},
        };
    }

    const state = {
        hash,
        scrolled: false,
        elements: {},
        callCount,
        callsCompleted: 0,
    };

    return {
        onCallCompleted: () => {
            state.callsCompleted++;
            const node = state.elements[state.hash];

            if (node && !state.scrolled && state.callsCompleted >= state.callCount) {
                node.scrollIntoView();
                state.scrolled = true;
            }
        },
        registerDeepLink: (node: Element, moduleHash: string) => {
            state.elements[moduleHash] = node;
        },
    };
}

export const mailMessages = defineMessages({
    subject: {
        id: 'abt.interiorsProfile.photo.share.email.subject',
        defaultMessage: 'Design Inpsiration on 1stdibs',
    },
    body: {
        id: 'abt.interiorsProfile.photo.share.email.body',
        defaultMessage: 'Check out {firmName}\'s "{projectName}" project on 1stdibs. {projectUrl}',
    },
    pinterest: {
        id: 'abt.interiorsProfile.share.pinterest',
        defaultMessage: 'See more of {firmName}\'s "{projectName}" on 1stdibs',
    },
});

// trailing slash can be removed once
// https://1stdibs.atlassian.net/browse/TRADE-2508
// is fixed
export const getSharePath = (projectUrl: string) => `${SV.get('localeOrigin')}${projectUrl}/`;

export const prepShareWindow = () => {
    const width = 665;
    const height = 500;

    const windowOptions = {
        scrollbars: 'yes',
        resizable: 'no',
        toolbar: 'no',
        height: height,
        width: width,
        top: 0,
        left: 0,
    };

    const screenHeight = screen.height;
    const screenWidth = screen.width;

    windowOptions.left = Math.round(screenWidth / 2 - width / 2);

    if (screenHeight > height) {
        windowOptions.top = Math.round(screenHeight / 2 - height / 2);
    }
    const windowString = Object.keys(windowOptions)
        .reduce((prev, curr) => {
            prev.push(`${curr}=${windowOptions[curr]}`);
            return prev;
        }, [])
        .join(',');

    return windowString;
};

type CommonPinterestShare = {|
    url: string,
    photoPath: string,
|};

type ShareWithDescription = {|
    ...CommonPinterestShare,
    description: string,
|};

type ShareWithFirmAndProject = {|
    ...CommonPinterestShare,
    firmName: string,
    projectName: string,
    intl: intlShape,
|};

type PinterestShare = ShareWithDescription | ShareWithFirmAndProject;

export function shareViaPinterest(input: PinterestShare) {
    const windowString = prepShareWindow();
    const pinWindow = window.open('', 'pinterest', windowString);

    let description = null;
    if (input.description) {
        description = input.description;
    } else if (input.intl) {
        const { firmName, projectName } = input;
        description = input.intl.formatMessage(mailMessages.pinterest, {
            firmName,
            projectName,
        });
    }

    if (!description) {
        return;
    }

    pinWindow.location = `https://pinterest.com/pin/create/button/?${stringify({
        url: getSharePath(input.url),
        media: input.photoPath,
        description,
    })}`;
}

export function shareViaTwitter(text: string, url: string): void {
    const windowString = prepShareWindow();
    const tweetWindow = window.open('', 'twitter', windowString);
    const windowOptions = { text, url };

    tweetWindow.location = `https://twitter.com/intent/tweet?${stringify(windowOptions)}`;
}

export function shareViaFacebook(photoImgPath: ?string) {
    if (!window.FB || !window.FB.ui || !photoImgPath) {
        console.error('Could not load FB share');
        return;
    }

    window.FB.ui({
        method: 'stream.share',
        href: window.location.href,
        image: photoImgPath,
    });
}

type EmailShare = {|
    subject: string,
    body: string,
    url: string,
|};

export function shareViaEmail({ subject, body, url }: EmailShare) {
    const subjectLine = encodeURIComponent(subject);
    const bodyText = `${encodeURIComponent(body)}%0D%0A%0D%0A${url}`;

    window.location.href = `mailto:?subject=${subjectLine}&body=${bodyText}`;
}

/**
 * Log an error to NewRelic (if available); otherwise, log to console.error.
 * @param {Error | string} error
 * @param [string] message Optional prefix to console log.
 */
export function logError(error: string | Error, message?: string = 'Exception caught:') {
    if (typeof error === 'string') {
        error = new Error(error);
    }

    noticeError(error, message);
}

/**
 * GraphQL objects that we expect somewhere but came back null. Unexpected
 * because typically they're only marked as nullable because of generated
 * flow types
 */
type UnexpectedNullGraphQLObjectName = 'project' | 'tradeFirm';

/**
 * Checks if a value is null that is actually not expected to be null but was
 * generated as such from the GraphQL schema. Can check fields as well as
 * objects.
 */
export function logUnexpectedNullGraphQLError(
    objectName: UnexpectedNullGraphQLObjectName,
    fieldName: ?string
) {
    if (fieldName) {
        logError(`Expected field "${fieldName}" on GraphQL object "${objectName}" to not be null`);
        return;
    }
    logError(`Expected GraphQL object "${objectName}", got null instead`);
}
